import type { SeriesKey } from './series'
import { FOUNDERS } from './catalog/founders.ts'
import { FRONTIER } from './catalog/frontier.ts'
import { GREAT_MINDS } from './catalog/great-minds.ts'
import { ROTATION } from './catalog/rotation.ts'

// Curated copy for each set. Counts, parts and prices come from the
// generated model data (sets.ts joins the two), so nothing here has to be
// kept in sync by hand when a model changes.

export interface SetCopy {
  slug: string
  series: SeriesKey
  // Position in the series, printed on the booklet as "No. 01".
  number: number
  name: string
  maker: string
  year: number
  // One line for the shelf and the booklet cover.
  tagline: string
  // Two or three sentences for the booklet's first spread.
  story: string
  age: string
  // Booklet cover field, flood colour when opened, ink on that colour,
  // build-view ground and a glow/accent colour.
  palette: {
    cover: string
    flood: string
    ink: string
    ground: string
    accent: string
  }
  // A flat tile mosaic on a baseplate (the rotation series): the booklet
  // draws its step art as a top-down chart instead of an isometric still,
  // and bags one per build section (booklet.ts, MosaicChart.tsx). An
  // explicit flag rather than inferring from `series`, since a future
  // series could mix mosaics with ordinary builds.
  mosaic?: boolean
  // Words a prompt might use for this device, for the generator's matching.
  aliases: string[]
}

export const SERIES_NAME = 'Hardware Icons'
export const BRAND = 'bricks by arne'

const HARDWARE: SetCopy[] = [
  {
    slug: 'macintosh-128k',
    series: 'hardware',
    number: 1,
    name: 'Macintosh 128K',
    maker: 'Apple',
    year: 1984,
    tagline: 'The computer for the rest of us, in tan bricks.',
    story:
      'The first Mac put a mouse, a bitmapped screen and a smiling boot icon in a beige box you could carry by its handle. Here every face of the case is a skin built flat and stood up on sideways studs. The bezel’s corners are rounded, the screen sits two plates behind it, and behind the bezel the case steps in and curves away at every edge, with the handle slot and vents on top and the ports on the back. The keyboard and one-button mouse wait on the desk.',
    age: '14+',
    palette: {
      cover: '#E8DCC2',
      flood: '#D8C49A',
      ink: '#2A2418',
      ground: '#E9E1CF',
      accent: '#C9A86A',
    },
    aliases: ['mac', 'macintosh', 'apple computer', '1984', 'classic mac'],
  },
  {
    slug: 'imac-g3',
    series: 'hardware',
    number: 2,
    name: 'iMac G3',
    maker: 'Apple',
    year: 1998,
    tagline: 'Bondi Blue, rounded, carried by its handle.',
    story:
      'The iMac turned a beige category into candy. At one to three, the white face is built flat and stood up on side studs, the screen two plates deep with the Mac OS start-up box on it. Behind it the Bondi shell rolls over from its top down to the back in one curve: a long curved slope, a shorter one tucked under its tail, a 45-degree slope, then curved slopes hung sideways on the back of the core, over a white band, a clear stripe and the clear carry handle. The keyboard and hockey-puck mouse sit on their clear cables.',
    age: '14+',
    palette: {
      cover: '#9FD6D2',
      flood: '#2FA8A6',
      ink: '#04302F',
      ground: '#CDEBE9',
      accent: '#15A0A2',
    },
    aliases: ['imac', 'bondi', 'g3', 'apple desktop', 'all in one'],
  },
  {
    slug: 'ipod-classic',
    series: 'hardware',
    number: 3,
    name: 'iPod Classic',
    maker: 'Apple',
    year: 2007,
    tagline: 'Your entire music library. One click wheel.',
    story:
      'A hard drive, a white face and a wheel you could scroll with a thumb. This one is life size, eight studs by thirteen and four plates thick: two of steel, two of white. The click wheel is an inverted dish with a round tile for the centre button, the menu sits in a black-bordered screen, and it stands in its white dock with the earbuds coiled beside it.',
    age: '10+',
    palette: {
      cover: '#F1F1EE',
      flood: '#D9DBDD',
      ink: '#1D2226',
      ground: '#E6E8EA',
      accent: '#9AA3AA',
    },
    aliases: ['ipod', 'click wheel', 'mp3 player', 'music player'],
  },
  {
    slug: 'iphone-2007',
    series: 'hardware',
    number: 4,
    name: 'iPhone',
    maker: 'Apple',
    year: 2007,
    tagline: 'An iPod, a phone and an internet communicator.',
    story:
      'One button, one sheet of glass, aluminium on the back. Life size this time, eight studs by fourteen and four plates thick, standing in the dock it came with. The home screen is twelve apps and a dock of four, each a round tile on the wallpaper, and a jumper plate centres the home button between two studs.',
    age: '10+',
    palette: {
      cover: '#2B2E33',
      flood: '#3A3F46',
      ink: '#F2F2F2',
      ground: '#DADDE1',
      accent: '#8C939C',
    },
    aliases: ['iphone', 'smartphone', 'phone', 'apple phone', '2007'],
  },
  {
    slug: 'newton-messagepad',
    series: 'hardware',
    number: 5,
    name: 'Newton MessagePad',
    maker: 'Apple',
    year: 1993,
    tagline: 'The handheld that was early to everything.',
    story:
      'Handwriting recognition, a green screen and a stylus in a slot, fourteen years before the iPhone. Life size, fourteen studs by twenty-three, built lying down and stood on a black stand. The Notepad is recessed a plate with its scrawl and the silkscreened icon strip beneath, the rainbow Apple and speaker along the bottom, and the lid lies beside it with the stylus.',
    age: '12+',
    palette: {
      cover: '#9DB0A2',
      flood: '#6F8C7B',
      ink: '#0F1B14',
      ground: '#D6E0D8',
      accent: '#5C7A67',
    },
    aliases: ['newton', 'messagepad', 'pda', 'palm', 'handheld', 'stylus'],
  },
  {
    slug: 'game-boy',
    series: 'hardware',
    number: 6,
    name: 'Game Boy',
    maker: 'Nintendo',
    year: 1989,
    tagline: 'Life size, leaning back, Tetris in the slot beside it.',
    story:
      'Built at one to one, eleven studs by eighteen, so it’s the size of the one in your drawer. The face is laid flat and tiled: pea-soup screen in a dark bezel, black D-pad and magenta A and B a plate proud, speaker slots stepped in grille tiles, and the famous corner rounded all the way through the case. It leans on a low black easel next to a Tetris Game Pak.',
    age: '10+',
    palette: {
      cover: '#C9C9C4',
      flood: '#8F9A6B',
      ink: '#1E2410',
      ground: '#E3E4DC',
      accent: '#8E2C6C',
    },
    aliases: ['gameboy', 'game boy', 'dmg', 'nintendo handheld', 'tetris'],
  },
  {
    slug: 'nes',
    series: 'hardware',
    number: 7,
    name: 'NES',
    maker: 'Nintendo',
    year: 1985,
    tagline: 'The gray box that brought games back.',
    story:
      'Designed to look like a VCR so shops would stock it: light grey over a dark band, a front-loading door and a ribbed top. Every face is a tiled panel on sideways studs, grille tiles do the ribs, and a controller on its cable and a Game Pak sit beside it.',
    age: '12+',
    palette: {
      cover: '#D3D3D3',
      flood: '#B8B8B8',
      ink: '#1A1A1A',
      ground: '#E7E7E7',
      accent: '#C8201E',
    },
    aliases: ['nes', 'nintendo entertainment system', 'famicom', 'mario'],
  },
  {
    slug: 'nintendo-64',
    series: 'hardware',
    number: 8,
    name: 'Nintendo 64',
    maker: 'Nintendo',
    year: 1996,
    tagline: 'Charcoal curves and a four-colour logo.',
    story:
      'A sculpted charcoal shell with a cartridge slot on top and the controller ports lined up in front. At one to two, curved slopes roll every edge of the black body over to the deck, Super Mario 64 stands in the slot with its label built into the bricks, the four-colour logo is a tile panel on side studs, and the three-pronged controller lies beside it.',
    age: '12+',
    palette: {
      cover: '#4A4D52',
      flood: '#35383C',
      ink: '#F0F0F0',
      ground: '#D9DADC',
      accent: '#1F5FB8',
    },
    aliases: ['n64', 'nintendo 64', 'mario 64', 'goldeneye', 'console'],
  },
  {
    slug: 'playstation',
    series: 'hardware',
    number: 9,
    name: 'PlayStation',
    maker: 'Sony',
    year: 1994,
    tagline: 'Gray, flat and round in the middle.',
    story:
      'A CD drive under a round lid, three buttons, two memory card slots and the controller on its cable. The lid is an 8 x 8 round plate under a 6 x 6 round tile, its rim studs a grip ring; the flanks are tiled panels on sideways studs, with grille-tile vents on the right.',
    age: '12+',
    palette: {
      cover: '#CFCFD2',
      flood: '#A9ABB2',
      ink: '#15161A',
      ground: '#E4E5E8',
      accent: '#3B5BA8',
    },
    aliases: ['playstation', 'ps1', 'psx', 'sony console'],
  },
  {
    slug: 'walkman-tps-l2',
    series: 'hardware',
    number: 10,
    name: 'Walkman TPS-L2',
    maker: 'Sony',
    year: 1979,
    tagline: 'Life size, standing up, tape in the window.',
    story:
      'The first Walkman had two headphone jacks and an orange button that talked over the music. This one is life size, eleven studs by seventeen and nine plates thick, built lying down and stood on a black stand. The door stands a plate proud of the blue face with the tape’s reels in its window, the keys step out of the silver end, and a C60 and the orange-padded headphones lie beside it.',
    age: '10+',
    palette: {
      cover: '#6F8FC9',
      flood: '#3A5FA6',
      ink: '#F4F4F4',
      ground: '#D7E0F0',
      accent: '#E87A1E',
    },
    aliases: ['walkman', 'cassette', 'tape player', 'sony walkman'],
  },
  {
    slug: 'braun-sk4',
    series: 'hardware',
    number: 11,
    name: 'Braun SK4',
    maker: 'Braun',
    year: 1956,
    tagline: 'Snow White’s coffin, with the lid up.',
    story:
      'Dieter Rams and Hans Gugelot put a radio and record player in a white box with wooden ends and a clear lid. The slotted front is plate edges with dark gaps between them, the elm cheeks are tiled panels on sideways studs, and the clear lid swings up on hinge bricks over the platter and tonearm.',
    age: '14+',
    palette: {
      cover: '#F2EFE8',
      flood: '#C9AE8A',
      ink: '#2A2118',
      ground: '#EEE8DD',
      accent: '#9C6B3F',
    },
    aliases: ['braun', 'sk4', 'dieter rams', 'record player', 'radiogram'],
  },
  {
    slug: 'leica-m3',
    series: 'hardware',
    number: 12,
    name: 'Leica M3',
    maker: 'Leica',
    year: 1954,
    tagline: 'Chrome, leatherette and a lens that sticks out.',
    story:
      'The rangefinder every other rangefinder is measured against. Life size, seventeen studs by twenty-five plates: stacked round bricks make the rounded ends, the whole front is built flat and stood up on side studs with its three windows in the chrome, and the lens stacks out of it in round bricks and plates. A lens cap and a roll of Tri-X sit beside it.',
    age: '14+',
    palette: {
      cover: '#D5D6D8',
      flood: '#2B2C2E',
      ink: '#F4F4F4',
      ground: '#E2E3E5',
      accent: '#B21E1E',
    },
    aliases: ['leica', 'm3', 'rangefinder', 'camera', 'film camera'],
  },
  {
    slug: 'polaroid-sx-70',
    series: 'hardware',
    number: 13,
    name: 'Polaroid SX-70',
    maker: 'Polaroid',
    year: 1972,
    tagline: 'Folds flat, opens into a picture machine.',
    story:
      'Edwin Land’s folding instant camera, opened: the chrome-and-leather cover swung up from the back of the base at the hinge angle, black bellows pleated underneath, the viewfinder hood at the apex, the lens on its board and a print sliding out of the slot below.',
    age: '14+',
    palette: {
      cover: '#C8A583',
      flood: '#7A4E2D',
      ink: '#FFF6EA',
      ground: '#EFE3D5',
      accent: '#D9352B',
    },
    aliases: ['polaroid', 'sx-70', 'sx70', 'instant camera'],
  },
  {
    slug: 'roland-tr-808',
    series: 'hardware',
    number: 14,
    name: 'Roland TR-808',
    maker: 'Roland',
    year: 1980,
    tagline: 'Sixteen steps from red to white.',
    story:
      'A drum machine that flopped, then turned up on half the records since. At one to two, the whole control panel is built flat and tilted six degrees onto a stepped body between black end cheeks: eleven columns of orange and white knobs over cream labels, the orange Rhythm Composer line, and sixteen step keys running red, orange, yellow, white, each with a trans-red LED.',
    age: '12+',
    palette: {
      cover: '#3C3D40',
      flood: '#2A2B2E',
      ink: '#F4F1EA',
      ground: '#E3E1DC',
      accent: '#E0612A',
    },
    aliases: ['808', 'tr-808', 'roland', 'drum machine', 'beat'],
  },
  {
    slug: 'op-1',
    series: 'hardware',
    number: 15,
    name: 'OP-1',
    maker: 'Teenage Engineering',
    year: 2011,
    tagline: 'A synth, a sampler and four coloured knobs.',
    story:
      'An aluminium slab with a tiny screen, a two-octave keyboard and four knobs in blue, green, white and orange. Life size, thirty-five studs by thirteen: a round plate’s studs make the speaker grille, the four encoders are round bricks (the real ones take LEGO shafts), and every key is a round tile standing a plate proud of the face.',
    age: '12+',
    palette: {
      cover: '#DADCDD',
      flood: '#B9BDC0',
      ink: '#121314',
      ground: '#EBECED',
      accent: '#E86A1C',
    },
    aliases: ['op-1', 'op1', 'teenage engineering', 'synth', 'synthesizer'],
  },
]

// Every set, series by series; `number` counts within a series.
export const CATALOG: SetCopy[] = [
  ...HARDWARE,
  ...FOUNDERS,
  ...FRONTIER,
  ...GREAT_MINDS,
  ...ROTATION,
]
